import { ApiRequestError, type ApiTransport, type ApiTransportRequest } from './transport'

export class HttpTransport implements ApiTransport {
  constructor(private readonly baseUrl: string) {}

  private getAuthToken(): string | null {
    return localStorage.getItem('cloutyspace_auth_token')
  }

  private buildHeaders(data: unknown, customHeaders?: HeadersInit): Record<string, string> {
    const headers = new Headers()

    if (!(data instanceof FormData)) {
      headers.set('Content-Type', 'application/json')
    }

    if (customHeaders) {
      new Headers(customHeaders).forEach((value, key) => headers.set(key, value))
    }

    const token = this.getAuthToken()
    if (token) {
      headers.set('Authorization', `Bearer ${token}`)
    }

    return Object.fromEntries(headers.entries())
  }

  private buildUrl(endpoint: string, params?: Record<string, unknown>): string {
    const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`
    const origin = typeof window === 'undefined' ? 'http://localhost' : window.location.origin
    const url = this.baseUrl
      ? new URL(`${this.baseUrl}${normalizedEndpoint}`)
      : new URL(normalizedEndpoint, origin)

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.set(key, String(value))
        }
      })
    }

    return url.toString()
  }

  private buildBody(data: unknown): BodyInit | undefined {
    if (data === undefined || data === null) {
      return undefined
    }

    if (data instanceof FormData) {
      return data
    }

    return JSON.stringify(data)
  }

  async request<T>({ method, endpoint, params, data, options }: ApiTransportRequest): Promise<T> {
    const url = this.buildUrl(endpoint, params)

    try {
      const response = await fetch(url, {
        ...options,
        method,
        headers: this.buildHeaders(data, options?.headers),
        body: method === 'GET' || method === 'DELETE' ? undefined : this.buildBody(data),
      })

      const responseData = await this.parseResponse(response)

      if (!response.ok) {
        throw new ApiRequestError({
          message: this.resolveErrorMessage(response.status, responseData),
          status: response.status,
          code: this.resolveErrorCode(responseData),
          data: responseData,
        })
      }

      return responseData as T
    } catch (error) {
      if (error instanceof ApiRequestError) {
        throw error
      }

      throw new ApiRequestError({
        message: error instanceof Error ? error.message : 'Network request failed',
        status: 0,
        data: error,
      })
    }
  }

  private async parseResponse(response: Response): Promise<unknown> {
    if (response.status === 204) {
      return undefined
    }

    const contentType = response.headers.get('content-type')
    if (contentType?.includes('application/json')) {
      return response.json()
    }

    const text = await response.text()
    return text ? { message: text } : undefined
  }

  private resolveErrorMessage(status: number, responseData: unknown): string {
    if (responseData && typeof responseData === 'object') {
      if ('message' in responseData && typeof responseData.message === 'string') {
        return responseData.message
      }

      if ('error' in responseData && typeof responseData.error === 'string') {
        return responseData.error
      }
    }

    return `HTTP Error: ${status}`
  }

  private resolveErrorCode(responseData: unknown): string | undefined {
    if (responseData && typeof responseData === 'object' && 'code' in responseData && typeof responseData.code === 'string') {
      return responseData.code
    }

    return undefined
  }
}
